import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);

  // Configure axios defaults
  useEffect(() => {
    const token = localStorage.getItem('wedding_admin_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, []);

  useEffect(() => {
    // Check if user is already authenticated (from localStorage)
    const token = localStorage.getItem('wedding_admin_token');
    if (token) {
      verifyToken();
    } else {
      setIsLoading(false);
    }
  }, []);

  const verifyToken = async () => {
    try {
      const response = await axios.get('/api/auth/verify');
      if (response.data.success) {
        setIsAuthenticated(true);
        setUser(response.data.data.user);
      } else {
        // Token is invalid, remove it
        localStorage.removeItem('wedding_admin_token');
        delete axios.defaults.headers.common['Authorization'];
      }
    } catch (error) {
      // Token is invalid or expired, remove it
      localStorage.removeItem('wedding_admin_token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post('/api/auth/login', {
        username,
        password
      });

      if (response.data.success) {
        const { token, user } = response.data.data;
        
        // Store token and set axios header
        localStorage.setItem('wedding_admin_token', token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        setIsAuthenticated(true);
        setUser(user);
        
        return { success: true };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      return { success: false, message };
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint to invalidate token on server side
      await axios.post('/api/auth/logout');
    } catch (error) {
      // Even if logout fails on server, we still logout locally
      console.error('Logout error:', error);
    } finally {
      // Clear local storage and state
      localStorage.removeItem('wedding_admin_token');
      delete axios.defaults.headers.common['Authorization'];
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  const value = {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
